import { useMemo } from 'react'
import { useApiConfig } from '../../../hooks/useApiConfig'

const MONTH_NAMES = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro']

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate()
}

function toDateString(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseDateString(date) {
  const [y, m, d] = date.split('-').map(Number)
  return { year: y, month: m, day: d }
}

export function DayPeriodPicker({ date, onChange, obsType, isAktual, onAktual, headerExtra }) {
  const config = useApiConfig()
  const { year, month, day } = parseDateString(date)

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const currentDay = now.getDate()

  const { minYear, minMonth, minDay } = useMemo(() => {
    const raw = config.backfillMinDate?.[obsType]
    if (!raw) return { minYear: 1900, minMonth: 1, minDay: 1 }
    const d = new Date(raw)
    return { minYear: d.getUTCFullYear(), minMonth: d.getUTCMonth() + 1, minDay: d.getUTCDate() }
  }, [config, obsType])

  const yearWindow = useMemo(() => {
    const start = year <= currentYear && year >= currentYear - 4
      ? currentYear - 4
      : year - 2
    return Array.from({ length: 5 }, (_, i) => start + i).filter(y => y >= minYear && y <= currentYear)
  }, [year, currentYear, minYear])

  const canGoBackYear = yearWindow[0] > minYear
  const canGoForwardYear = yearWindow[yearWindow.length - 1] < currentYear

  function shiftYearWindow(delta) {
    const newStart = yearWindow[0] + delta * 5
    const clamped = Math.max(minYear, Math.min(currentYear, newStart))
    const clampedDay = Math.min(day, daysInMonth(clamped, month))
    onChange(toDateString(clamped, month, clampedDay))
  }

  const availableMonths = useMemo(() => {
    const lo = year === minYear ? minMonth : 1
    const hi = year === currentYear ? currentMonth : 12
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  }, [year, minYear, minMonth, currentYear, currentMonth])

  const availableDays = useMemo(() => {
    const lo = (year === minYear && month === minMonth) ? minDay : 1
    const hi = (year === currentYear && month === currentMonth) ? currentDay : daysInMonth(year, month)
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  }, [year, month, minYear, minMonth, minDay, currentYear, currentMonth, currentDay])

  const canGoPrevMonth = !(year === minYear && month === minMonth)
  const canGoNextMonth = !(year === currentYear && month === currentMonth)

  function handleMonthChange(newYear, newMonth) {
    const isBack = newYear < year || (newYear === year && newMonth < month)
    if (isBack) {
      const maxDay = newYear === currentYear && newMonth === currentMonth ? currentDay : daysInMonth(newYear, newMonth)
      const minD   = newYear === minYear && newMonth === minMonth ? minDay : 1
      onChange(toDateString(newYear, newMonth, Math.max(minD, maxDay)))
    } else {
      const minD = newYear === minYear && newMonth === minMonth ? minDay : 1
      onChange(toDateString(newYear, newMonth, minD))
    }
  }

  function handlePrevMonth() {
    if (month === 1) handleMonthChange(year - 1, 12)
    else handleMonthChange(year, month - 1)
  }

  function handleNextMonth() {
    if (month === 12) handleMonthChange(year + 1, 1)
    else handleMonthChange(year, month + 1)
  }

  const isAtMinDate = year === minYear && month === minMonth && day === minDay
  const isAtMaxDate = year === currentYear && month === currentMonth && day === currentDay

  function handlePrevDay() {
    if (day > 1) {
      onChange(toDateString(year, month, day - 1))
    } else {
      const prevMonth = month === 1 ? 12 : month - 1
      const prevYear = month === 1 ? year - 1 : year
      onChange(toDateString(prevYear, prevMonth, daysInMonth(prevYear, prevMonth)))
    }
  }

  function handleNextDay() {
    const maxDay = daysInMonth(year, month)
    if (day < maxDay) {
      onChange(toDateString(year, month, day + 1))
    } else {
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      onChange(toDateString(nextYear, nextMonth, 1))
    }
  }

  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <div className="flex items-start gap-4">
        <div className="flex flex-col gap-0.5 min-w-0">
          {/* Year row */}
          <div className="flex items-center gap-1">
            <button
              className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
              onClick={() => shiftYearWindow(-1)}
              disabled={!canGoBackYear}
            >‹</button>
            {yearWindow.map(y => (
              <button
                key={y}
                onClick={() => {
                  if (y === year) return
                  const newMonth = y < year
                    ? (y === currentYear ? currentMonth : 12)
                    : (y === minYear ? minMonth : 1)
                  handleMonthChange(y, newMonth)
                }}
                className={`px-3 py-px rounded text-sm font-medium transition-colors ${
                  y === year ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {y}
              </button>
            ))}
            <button
              className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
              onClick={() => shiftYearWindow(1)}
              disabled={!canGoForwardYear}
            >›</button>
          </div>

          {/* Month row */}
          <div className="flex items-center gap-1 min-w-0">
            <button
              className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
              onClick={handlePrevMonth}
              disabled={!canGoPrevMonth}
            >‹</button>
            <div className="flex flex-nowrap gap-1 overflow-x-auto">
              {availableMonths.map(m => (
                <button
                  key={m}
                  onClick={() => handleMonthChange(year, m)}
                  className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${
                    m === month ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {MONTH_NAMES[m - 1]}
                </button>
              ))}
            </div>
            <button
              className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
              onClick={handleNextMonth}
              disabled={!canGoNextMonth}
            >›</button>
          </div>
        </div>

        {headerExtra && <div className="ml-auto flex flex-wrap items-start gap-8">{headerExtra}</div>}
      </div>

      {/* Day row */}
      <div className="flex items-center gap-1 min-w-0">
        <button
          className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
          onClick={handlePrevDay}
          disabled={isAtMinDate}
        >‹</button>
        <div className="flex flex-nowrap gap-1 overflow-x-auto">
          {availableDays.map(d => (
            <button
              key={d}
              onClick={() => onChange(toDateString(year, month, d))}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors min-w-[1.75rem] text-center ${
                !isAktual && d === day ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {d}
            </button>
          ))}
          {onAktual && year === currentYear && month === currentMonth && (
            <button
              onClick={onAktual}
              className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors border ${
                isAktual
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'text-gray-500 border-gray-300 hover:bg-gray-100'
              }`}
            >
              aktuální
            </button>
          )}
        </div>
        <button
          className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
          onClick={handleNextDay}
          disabled={isAtMaxDate}
        >›</button>
      </div>
    </div>
  )
}
